import {
  getAtomicUnitBang,
  getUnit,
  normalizeUnit,
  NullUnit,
} from './registry';
import { Unit } from './types';
import { UnknownUnitError } from './UnknownUnitError';
import { divideUnits, multiplyUnits } from './operations';

type ParseUnitStringOptions = {
  preserveName?: boolean;
};

/*
 * Parses a unit string that may contain '/', '-', and '^' which are interpreted
 * as division, multiplication, and exponentiation respectively.
 *
 * Known aliases will be resolved to a canonical name (e.g. "km" becomes
 * "kilometer"). Also, to facilitate conversions, some units are converted to
 * lower-level SI base units (e.g. "joule" becomes "kg-m^2/s^2"), unless you
 * set `preserveName: true`.
 */
export function parseUnitString(
  s: string,
  { preserveName = false }: ParseUnitStringOptions = {}
): Unit {
  if (!preserveName) {
    // Look for an explicitly-defined unit to account for weird cases
    const explicitUnit = getUnit(s);

    if (explicitUnit) {
      return explicitUnit;
    }
  }

  const splitByDivision = s.replace(/\s/g, '').split('/');
  if (splitByDivision.length === 0) {
    throw new UnknownUnitError(s);
  }

  const divisionParts = splitByDivision.map(parseMultUnit);
  const denormalizedUnit = divisionParts
    .slice(1)
    .reduce((acc, part) => divideUnits(acc, part), divisionParts[0]);
  return normalizeUnit(denormalizedUnit, [], { preserveName });
}

/**
 * Like `parseUnitString`, but if the unit is invalid returns null instead of
 * throwing an error.
 */
export function gentlyParseUnitString(
  s: string,
  options: ParseUnitStringOptions = {}
): Unit | null {
  try {
    return parseUnitString(s, options);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error instanceof UnknownUnitError) {
      return null;
    }
    throw error;
  }
}

// Parses a unit string that may contain '^' indicating an exponent
function parseExpUnit(s: string): Unit {
  if (s === '') {
    throw new UnknownUnitError(s);
  }

  const parts = s.split('^');
  if (parts.length === 1) {
    return new Map([[getAtomicUnitBang(parts[0]), 1]]);
  } else if (parts.length === 2) {
    const pow = Number.parseInt(parts[1]);
    if (pow === 0 || Number.isNaN(0)) {
      throw new UnknownUnitError(s);
    }
    return new Map([[getAtomicUnitBang(parts[0]), pow]]);
  }
  throw new UnknownUnitError(s);
}

// Parses a unit string that may contain '-' indicating multiplication
function parseMultUnit(s: string): Unit {
  if (s === '') {
    throw new UnknownUnitError(s);
  }

  const parts = s.split('-').map(parseExpUnit);

  if (parts.length < 1) {
    throw new UnknownUnitError(s);
  }

  return parts.reduce((acc, part) => {
    return multiplyUnits(acc, part);
  }, NullUnit);
}
